<template>
    <div class="travel-quote-step medical-questions">
        <quote-step-header
            :title="subStepFields.title"
            :subtitle="subStepFields.subtitle"
        ></quote-step-header>
        <form class="quote-form" @submit.prevent="goNext">
            <div
                class="question-item"
                v-for="(value, question, index) in viewForm"
                :key="index"
            >
                <div class="question-item__inner">
                    <p>{{ trans(`medical_questions[${index}]`) }}</p>
                    <app-text-radio-buttons
                        :circle="true"
                        :items="iconItems"
                        v-model="form[stepName].medical_questions[question]"
                    ></app-text-radio-buttons>
                </div>
                <medical-travelers
                    v-if="form[stepName].medical_questions[question]"
                    :trans="trans"
                    :travelers="travelers"
                    :question="question"
                ></medical-travelers>
            </div>
            <div v-if="femaleTravelers.length > 0" class="question-item">
                <div class="question-item__inner">
                    <p>{{ trans(`medical_questions[4]`) }}</p>
                    <app-text-radio-buttons
                        :circle="true"
                        :items="iconItems"
                        v-model="form[stepName].medical_questions.pregnancy"
                    ></app-text-radio-buttons>
                </div>
                <medical-travelers
                    v-if="form[stepName].medical_questions.pregnancy"
                    label="medical_travelers.which_females"
                    :trans="trans"
                    :travelers="femaleTravelers"
                    question="pregnancy"
                ></medical-travelers>
            </div>
            <template v-if="showPregnantExtraFields">
                <div class="question-item">
                    <div class="question-item__inner">
                        <p>
                            {{
                                trans("pregnancy_questions.high_risk_pregnancy")
                            }}
                        </p>
                        <app-text-radio-buttons
                            :circle="true"
                            :items="iconItems"
                            v-model="
                                form[stepName].medical_questions
                                    .high_risk_pregnancy
                            "
                        ></app-text-radio-buttons>
                    </div>
                    <medical-travelers
                        v-if="
                            form[stepName].medical_questions.high_risk_pregnancy
                        "
                        label="medical_travelers.which_females"
                        :trans="trans"
                        :travelers="femaleTravelers"
                        question="high_risk_pregnancy"
                    ></medical-travelers>
                </div>
                <div class="question-item">
                    <div class="question-item__inner">
                        <p>{{ trans("pregnancy_questions.pregnancy_week") }}</p>
                        <div class="flex flex-col gap-4">
                            <div
                                v-for="traveler in femaleTravelers"
                                :key="traveler.details.id"
                                class="pregnant-input-container"
                            >
                                <p>{{ traveler.details.name }}</p>
                                <app-input
                                    class="pregnant-input"
                                    type="number"
                                    v-model="traveler.values.pregnancy_week"
                                ></app-input>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <div class="submit-row">
                <app-button :disabled="disabledConditions" :loading="loading">
                    {{ "quote.welcome.next" | __ }}
                </app-button>
            </div>
        </form>
    </div>
</template>

<script>
import QuoteStep from "../../../mixins/QuoteStep";
import AppIconRadioButton from "../../Forms/IconRadioButtons.vue";
import AppTextRadioButtons from "../../Forms/TextRadioButtons.vue";
import MedicalTravelers from "./MedicalTravelers.vue";

export default {
    name: "TravelQuoteMedicalQuestions",
    mixins: [QuoteStep],
    components: { AppIconRadioButton, MedicalTravelers, AppTextRadioButtons },
    props: {},
    data() {
        return {
            subSteps: ["default"],
            iconItems: [
                {
                    value: false,
                    label: this.trans("no")
                },
                {
                    value: true,
                    label: this.trans("yes")
                }
            ]
        };
    },
    computed: {
        canMoveNext() {
            if (Object.values(this.viewForm).includes(null)) return false;
            if (
                this.femaleTravelers.length > 0 &&
                this.form[this.stepName].medical_questions.pregnancy == null
            )
                return false;
            if (this.form[this.stepName].medical_questions.pregnancy) {
                const { high_risk_pregnancy } = this.form[
                    this.stepName
                ].medical_questions;
                if (high_risk_pregnancy == null) return false;
            }
            return true;
        },
        viewForm() {
            const form = { ...this.form[this.stepName].medical_questions };
            delete form.pregnancy;
            delete form.high_risk_pregnancy;
            delete form.pregnancy_week;
            return form;
        },
        showPregnantExtraFields() {
            return this.form[this.stepName].medical_questions.pregnancy;
        },
        femaleTravelers() {
            return this.travelers.filter(
                traveler => traveler.details.sex === this.constants.SEX_FEMALE
            );
        },
        travelers() {
            return (
                this?.form?.TravelQuoteTripInfo?.travelers.map(traveler => ({
                    details: traveler,
                    values: {
                        chronic_disease: false,
                        inquiry_process: false,
                        medical_condition_changed: false,
                        pills_regulary: false,
                        pregnancy: false,
                        high_risk_pregnancy: false,
                        pregnancy_week: false
                    }
                })) || []
            );
        },
        medicalQuestions() {
            return this.form[this.stepName].medical_questions;
        }
    },
    watch: {
        medicalQuestions: {
            handler(val) {
                Object.entries(val).forEach(([key, value]) => {
                    if (!value) {
                        this.travelers.forEach(traveler => {
                            traveler.values[key] = false;
                        });
                    }
                });
            },
            deep: true
        }
    },
    methods: {
        goNext() {
            this.form[this.stepName].travelers = this.travelers.map(
                traveler => ({
                    [traveler.details.id]: traveler.values
                })
            );
            this.next();
        }
    }
};
</script>

<style lang="scss" scoped>
.quote-form {
    gap: 10px;
}

.question-item {
    border-bottom: 1px solid rgba($color: $black, $alpha: 0.1);
    padding: 40px 0;

    .question-item__inner {
        display: flex;
        justify-content: space-between;
        align-items: center;

        @media (max-width: $mobile) {
            flex-direction: column;
            padding: 20px 0;
            gap: 10px;
            p {
                max-width: 100%;
            }
        }
    }

    p {
        max-width: 50%;
    }

    .pregnant-input-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
}
</style>
